import { fetcher, useClientPortalAPI } from "@certn/api";
import { components } from "@certn/api/generated-schemas/client-portal";
import * as Sentry from "@sentry/react";
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
} from "react";

type Features = components["schemas"]["UserFeatures"];

type PartialRecord<K extends keyof any, T> = {
  [P in K]?: T;
};

interface UserFeaturesContextProps {
  features: PartialRecord<Features["feature"], Features["value"]>;
  updateFeature: (
    feature: Features["feature"],
    value: Features["value"]
  ) => Promise<void>;
}

export const UserFeaturesContext = createContext<UserFeaturesContextProps>({
  features: {},
  updateFeature: async () => {},
});

export function UserFeaturesProvider(props: PropsWithChildren) {
  const { data, mutate } = useClientPortalAPI(
    "/api/internal/client-portal/me/features/",
    {},
    {
      onError(error) {
        Sentry.captureException(error);
      },
    }
  );

  const features = data?.reduce<UserFeaturesContextProps["features"]>(
    (previousValue, currentValue) => {
      return {
        ...previousValue,
        [currentValue.feature]: currentValue.value,
      };
    },
    {}
  );

  const optimisticUpdate = useCallback(
    (feature: Features["feature"], value: Features["value"]) => {
      return data?.map((f) => (f.feature === feature ? { ...f, value } : f));
    },
    [data]
  );

  const updateFeatureAPI = useCallback(
    async (feature: Features["feature"], value: Features["value"]) => {
      await fetcher.submit("/api/internal/client-portal/me/features/update/", {
        feature,
        value,
      });
    },
    []
  );

  const updateFeature = useCallback(
    async (feature: Features["feature"], value: Features["value"]) => {
      try {
        await mutate(
          async () => {
            await updateFeatureAPI(feature, value);
            return optimisticUpdate(feature, value);
          },
          {
            optimisticData: optimisticUpdate(feature, value),
            rollbackOnError: true,
          }
        );
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
    },
    [mutate, optimisticUpdate, updateFeatureAPI]
  );

  return (
    <UserFeaturesContext.Provider
      value={{ features: features || {}, updateFeature }}
    >
      {props.children}
    </UserFeaturesContext.Provider>
  );
}

export function useUserFeatures() {
  return useContext(UserFeaturesContext);
}
